import React, { useEffect } from 'react';
import { Toaster } from 'sonner';
import { StoreProvider, useStore } from '@/lib/store';
import SimnopsisBackground from '@/components/simnopsis/Background';
import Header from '@/components/simnopsis/Header';
import Hero from '@/components/simnopsis/Hero';
import UploadPanel from '@/components/simnopsis/UploadPanel';
import RecentRuns from '@/components/simnopsis/RecentRuns';
import ProcessingConsole from '@/components/simnopsis/ProcessingConsole';
import HighlightShowcase from '@/components/simnopsis/HighlightShowcase';
import UseCases from '@/components/simnopsis/UseCases';
import PrivacyStrip from '@/components/simnopsis/PrivacyStrip';
import Footer from '@/components/simnopsis/Footer';
import ScrollProgress from '@/components/simnopsis/ScrollProgress';
import BackToTop from '@/components/simnopsis/BackToTop';
import '@/App.css';

function App() {
  return (
    <StoreProvider>
      <AppShell />
    </StoreProvider>
  );
}

function AppShell() {
  const { state, setState, clearSession } = useStore();

  // IntersectionObserver reveal-on-scroll
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -60px 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [state.showcaseVisible, state.showcaseSkeleton]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable;
      if (isInput) return;
      if (e.key === '1' || e.key === '2' || e.key === '3' || e.key === '4') {
        if (!state.showcaseVisible) return;
        const map = { '1': 'stack', '2': 'cards', '3': 'timeline', '4': 'refine' };
        setState({ activeTab: map[e.key] });
        e.preventDefault();
      }
      if (e.key === 'Escape') {
        if (state.consoleMode === 'complete') {
          clearSession();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.showcaseVisible, state.consoleMode, setState, clearSession]);

  return (
    <div className="min-h-screen text-white">
      <SimnopsisBackground />
      <ScrollProgress />
      <Header />
      <main>
        <Hero />
        <UploadPanel />
        <RecentRuns />
        <ProcessingConsole />
        <HighlightShowcase />
        <UseCases />
        <PrivacyStrip />
      </main>
      <Footer />
      <BackToTop />
      <Toaster
        position="bottom-right"
        duration={4000}
        closeButton
        theme="dark"
        richColors={false}
      />
    </div>
  );
}

export default App;
